import React, { useEffect, useMemo, useState } from 'react';
import {
  Objective,
  Review,
  ReviewType,
  ReviewCycle,
  ReviewCycleStatus,
  ReviewWorkflowStatus,
  RatingScale,
  ReviewHistoryAction,
} from '../../types/pms';
import ReviewCycleSelector, { isHistoricalCycle } from './ReviewCycleSelector';
import { getRatingLabel } from '../../services/pmsRatingCalculator';
import {
  appendReviewHistory,
  buildReviewHistoryEntry,
  getDaysOverdue,
  getReviewWorkflowStatusLabel,
  inferReviewWorkflowStatus,
} from '../../services/pmsWorkflow';
import { GraphQLPMSService } from '../../services/graphqlPms';
import { ReportExportService } from '../../services/reportExport';
import {
  notifyReviewCycleLocked,
  notifyReviewCycleUnlocked,
  notifyReviewReopened,
} from '../../services/inAppNotification';
import {
  buildStatusDistribution,
  calculatePercentage,
  formatChartPercentage,
  OVERDUE_COLOR,
  REVIEW_STATUS_COLORS,
} from './hrAnalyticsCharts';
import './HRAdminDashboard.css';

interface HRAdminDashboardProps {
  allObjectives: Objective[];
  allReviews: Review[];
  reviewCycles: ReviewCycle[];
  selectedCycleId: string;
  onCycleChange: (cycleId: string) => void;
  onRefresh?: () => void;
}

interface DiagnosticRow {
  metric: string;
  source: string;
  formula: string;
  actual: number;
  expected: number;
  valid: boolean;
}

const STATUS_ORDER = [
  ReviewWorkflowStatus.DRAFT,
  ReviewWorkflowStatus.IN_PROGRESS,
  ReviewWorkflowStatus.SUBMITTED,
  ReviewWorkflowStatus.COMPLETED,
  ReviewWorkflowStatus.REOPEN_REQUESTED,
];

const RATING_COLORS = ['#15803d', '#0d9488', '#2563eb', '#f59e0b', '#dc2626'];
const FINAL_RATINGS = new Set<number>([
  RatingScale.OUTSTANDING,
  RatingScale.EXCEEDS_EXPECTATIONS,
  RatingScale.MEETS_EXPECTATIONS,
  RatingScale.NEEDS_IMPROVEMENT,
  RatingScale.UNSATISFACTORY,
]);

const hasFinalRating = (review: Review): review is Review & { overallRating: RatingScale } =>
  typeof review.overallRating === 'number' && FINAL_RATINGS.has(review.overallRating);

const formatDate = (value?: string) => {
  if (!value) return 'Not available';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? 'Not available' : date.toLocaleDateString();
};

const HRAdminDashboard: React.FC<HRAdminDashboardProps> = ({
  allReviews,
  reviewCycles,
  selectedCycleId,
  onCycleChange,
  onRefresh,
}) => {
  const [managerFilter, setManagerFilter] = useState('');
  const [reviewTypeFilter, setReviewTypeFilter] = useState('');
  const [reopenReason, setReopenReason] = useState('');
  const [selectedReviewId, setSelectedReviewId] = useState('');
  const [actionMessage, setActionMessage] = useState('');
  const [overdueOpen, setOverdueOpen] = useState(true);
  const [reopenOpen, setReopenOpen] = useState(false);
  const [diagnosticsOpen, setDiagnosticsOpen] = useState(false);

  const selectedCycle = reviewCycles.find((cycle) => cycle.id === selectedCycleId);
  const isHistorical = useMemo(
    () => isHistoricalCycle(reviewCycles, selectedCycleId),
    [reviewCycles, selectedCycleId]
  );

  const filteredReviews = useMemo(() => allReviews.filter((review) => {
    if (reviewTypeFilter && review.reviewType !== reviewTypeFilter) return false;
    return !managerFilter || review.reviewerName
      .toLowerCase()
      .includes(managerFilter.toLowerCase());
  }), [allReviews, managerFilter, reviewTypeFilter]);

  const dueDate = selectedCycle?.endYearDeadline || selectedCycle?.submissionDeadline;

  const analytics = useMemo(() => {
    const statusCounts = new Map<ReviewWorkflowStatus, number>(
      STATUS_ORDER.map((status) => [status, 0])
    );
    const ratingCounts = new Map<RatingScale, number>();
    const overdueRows: Array<{ review: Review; daysOverdue: number }> = [];
    let acknowledged = 0;
    let completed = 0;
    let reopenRequests = 0;

    filteredReviews.forEach((review) => {
      const status = inferReviewWorkflowStatus(review);
      statusCounts.set(status, (statusCounts.get(status) || 0) + 1);
      if (status === ReviewWorkflowStatus.SUBMITTED || status === ReviewWorkflowStatus.COMPLETED) {
        completed += 1;
      }
      if (review.isAcknowledged === true) acknowledged += 1;
      if (hasFinalRating(review)) {
        ratingCounts.set(review.overallRating, (ratingCounts.get(review.overallRating) || 0) + 1);
      }
      if (review.reopenRequest?.status === 'Pending') reopenRequests += 1;
      if (
        dueDate &&
        getDaysOverdue(dueDate) > 0 &&
        status !== ReviewWorkflowStatus.SUBMITTED &&
        status !== ReviewWorkflowStatus.COMPLETED
      ) {
        overdueRows.push({ review, daysOverdue: getDaysOverdue(dueDate) });
      }
    });

    const total = filteredReviews.length;
    const rated = Array.from(ratingCounts.values()).reduce((sum, count) => sum + count, 0);
    const statusTotal = Array.from(statusCounts.values()).reduce((sum, count) => sum + count, 0);
    return {
      total,
      completed,
      acknowledged,
      overdueRows,
      reopenRequests,
      rated,
      statusCounts,
      ratingCounts,
      statusTotal,
      completionPercentage: total ? Math.round((completed / total) * 100) : 0,
      acknowledgementRate: total ? Math.round((acknowledged / total) * 100) : 0,
    };
  }, [dueDate, filteredReviews]);

  const ratingDistribution = useMemo(() => [
    RatingScale.OUTSTANDING,
    RatingScale.EXCEEDS_EXPECTATIONS,
    RatingScale.MEETS_EXPECTATIONS,
    RatingScale.NEEDS_IMPROVEMENT,
    RatingScale.UNSATISFACTORY,
  ].map((rating, index) => ({
    rating,
    label: getRatingLabel(rating),
    count: analytics.ratingCounts.get(rating) || 0,
    percentage: calculatePercentage(analytics.ratingCounts.get(rating) || 0, analytics.rated),
    color: RATING_COLORS[index],
  })), [analytics.rated, analytics.ratingCounts]);

  const statusDistribution = useMemo(
    () => buildStatusDistribution(STATUS_ORDER, analytics.statusCounts, analytics.total),
    [analytics.statusCounts, analytics.total]
  );

  const completionTrend = useMemo(() => {
    const completedReviews = filteredReviews
      .filter((review) => {
        const status = inferReviewWorkflowStatus(review);
        return status === ReviewWorkflowStatus.SUBMITTED || status === ReviewWorkflowStatus.COMPLETED;
      })
      .map((review) => ({
        date: new Date(review.submittedAt || review.publishedAt || review.updatedAt || review.timestamp),
      }))
      .filter(({ date }) => !Number.isNaN(date.getTime()))
      .sort((a, b) => a.date.getTime() - b.date.getTime());

    const grouped = new Map<string, { label: string; count: number; time: number }>();
    completedReviews.forEach(({ date }) => {
      const key = `${date.getFullYear()}-${date.getMonth()}`;
      const current = grouped.get(key);
      grouped.set(key, {
        label: date.toLocaleDateString(undefined, { month: 'short', year: '2-digit' }),
        count: (current?.count || 0) + 1,
        time: new Date(date.getFullYear(), date.getMonth(), 1).getTime(),
      });
    });
    let cumulative = 0;
    return Array.from(grouped.values())
      .sort((a, b) => a.time - b.time)
      .map((entry) => ({ ...entry, cumulative: (cumulative += entry.count) }));
  }, [filteredReviews]);

  const diagnostics = useMemo<DiagnosticRow[]>(() => [
    {
      metric: 'Total Reviews', source: 'Review (getReviewsByReviewCycle)', formula: 'Count of filtered review records',
      actual: analytics.total, expected: filteredReviews.length, valid: analytics.total === filteredReviews.length,
    },
    {
      metric: 'Completed', source: 'Review.workflowStatus', formula: '(Submitted + Completed) / Total Reviews',
      actual: analytics.completed, expected: filteredReviews.filter((review) => {
        const status = inferReviewWorkflowStatus(review);
        return status === ReviewWorkflowStatus.SUBMITTED || status === ReviewWorkflowStatus.COMPLETED;
      }).length, valid: analytics.completed === filteredReviews.filter((review) => {
        const status = inferReviewWorkflowStatus(review);
        return status === ReviewWorkflowStatus.SUBMITTED || status === ReviewWorkflowStatus.COMPLETED;
      }).length,
    },
    {
      metric: 'Acknowledged', source: 'Review.isAcknowledged', formula: 'isAcknowledged === true / Total Reviews',
      actual: analytics.acknowledged, expected: filteredReviews.filter((review) => review.isAcknowledged === true).length,
      valid: analytics.acknowledged === filteredReviews.filter((review) => review.isAcknowledged === true).length,
    },
    {
      metric: 'Overdue', source: 'Review.workflowStatus + ReviewCycle.endYearDeadline/submissionDeadline', formula: 'Past due and not Submitted/Completed',
      actual: analytics.overdueRows.length,
      expected: filteredReviews.filter((review) => {
        const status = inferReviewWorkflowStatus(review);
        return Boolean(dueDate && getDaysOverdue(dueDate) > 0 && status !== ReviewWorkflowStatus.SUBMITTED && status !== ReviewWorkflowStatus.COMPLETED);
      }).length,
      valid: analytics.overdueRows.length === filteredReviews.filter((review) => {
        const status = inferReviewWorkflowStatus(review);
        return Boolean(dueDate && getDaysOverdue(dueDate) > 0 && status !== ReviewWorkflowStatus.SUBMITTED && status !== ReviewWorkflowStatus.COMPLETED);
      }).length,
    },
    {
      metric: 'Status Distribution', source: 'Review.workflowStatus/isAcknowledged/overallRating', formula: 'Sum of mutually exclusive inferred statuses',
      actual: analytics.statusTotal, expected: analytics.total, valid: analytics.statusTotal === analytics.total,
    },
    {
      metric: 'Rating Distribution', source: 'Review.overallRating', formula: 'Sum of reviews with a final overall rating',
      actual: ratingDistribution.reduce((sum, row) => sum + row.count, 0), expected: analytics.rated,
      valid: ratingDistribution.reduce((sum, row) => sum + row.count, 0) === analytics.rated,
    },
    {
      metric: 'Reopen Requests', source: 'Review.reopenRequest', formula: 'reopenRequest.status === Pending',
      actual: analytics.reopenRequests,
      expected: filteredReviews.filter((review) => review.reopenRequest?.status === 'Pending').length,
      valid: analytics.reopenRequests === filteredReviews.filter((review) => review.reopenRequest?.status === 'Pending').length,
    },
    {
      metric: 'Overdue Table', source: 'Derived overdue review rows', formula: 'Displayed table row count equals overdue KPI',
      actual: analytics.overdueRows.length, expected: analytics.overdueRows.length, valid: true,
    },
  ], [analytics, dueDate, filteredReviews, ratingDistribution]);

  useEffect(() => {
    const failed = diagnostics.filter((row) => !row.valid);
    console.groupCollapsed(`[HR Analytics Validation] ${selectedCycle?.name || selectedCycleId || 'No cycle'}`);
    console.table(diagnostics);
    console.info('Source records', filteredReviews);
    console.info('Overdue record IDs', analytics.overdueRows.map(({ review }) => review.id));
    console.table(statusDistribution.map((row) => ({
      status: getReviewWorkflowStatusLabel(row.key),
      count: row.count,
      percentage: row.percentage,
      color: row.color,
    })));
    console.info('Final-rated record IDs', filteredReviews.filter(hasFinalRating).map((review) => review.id));
    const invalidRatings = filteredReviews
      .filter((review) => review.overallRating != null && !hasFinalRating(review))
      .map((review) => ({ id: review.id, employeeName: review.employeeName, overallRating: review.overallRating }));
    if (invalidRatings.length) {
      console.warn('Ignored non-final rating values; valid final ratings are 1 through 5.', invalidRatings);
    }
    if (failed.length) console.error('Metric discrepancies detected', failed);
    else console.info('All dashboard metrics reconcile.');
    console.groupEnd();
  }, [analytics.overdueRows, diagnostics, filteredReviews, selectedCycle?.name, selectedCycleId, statusDistribution]);

  const handleLockCycle = async () => {
    if (!selectedCycleId) return;
    if (!window.confirm('Lock this objective cycle?\n\nEmployees and managers will no longer be able to modify objectives or reviews.')) return;
    setActionMessage('');
    const result = await GraphQLPMSService.lockObjectivesForCycle(selectedCycleId);
    if (result.success) {
      setActionMessage('✓ Review cycle locked successfully.');
      const recipients = [...new Set(filteredReviews.flatMap((review) => [review.reviewerId, review.employeeId]))];
      await notifyReviewCycleLocked(recipients, 'HR Admin', selectedCycle?.name || selectedCycleId);
      onRefresh?.();
    } else setActionMessage('✗ ' + (result.errors.join(' ') || 'Failed to lock cycle.'));
  };

  const handleUnlockCycle = async () => {
    if (!selectedCycleId) return;
    if (!window.confirm('Unlock this objective cycle?\n\nEditing and submissions will be allowed again for all employees and managers.')) return;
    setActionMessage('');
    const result = await GraphQLPMSService.unlockObjectivesForCycle(selectedCycleId);
    if (result.success) {
      setActionMessage('✓ Review cycle unlocked successfully.');
      const recipients = [...new Set(filteredReviews.flatMap((review) => [review.reviewerId, review.employeeId]))];
      await notifyReviewCycleUnlocked(recipients, 'HR Admin', selectedCycle?.name || selectedCycleId);
      onRefresh?.();
    } else setActionMessage('✗ ' + (result.errors.join(' ') || 'Failed to unlock cycle.'));
  };

  const handleReopenReview = async () => {
    const review = filteredReviews.find((item) => item.id === selectedReviewId);
    if (!review || !reopenReason.trim()) return;
    const now = new Date().toISOString();
    const reviewHistory = appendReviewHistory(review, buildReviewHistoryEntry(
      ReviewHistoryAction.REVIEW_REOPENED, 'hr-admin', 'HR Admin', review.workflowStatus, reopenReason.trim()
    ));
    const result = await GraphQLPMSService.updateReview(review.id, {
      workflowStatus: ReviewWorkflowStatus.IN_PROGRESS,
      reviewMetadata: {
        ...(review.reviewMetadata || {}), workflowStatus: ReviewWorkflowStatus.IN_PROGRESS,
        reviewHistory, reopenedBy: 'hr-admin', reopenedByName: 'HR Admin', reopenedAt: now,
        reopenReason: reopenReason.trim(),
        reopenRequest: review.reopenRequest
          ? { ...review.reopenRequest, status: 'Approved' as const, resolvedAt: now }
          : undefined,
      },
    });
    if (result.success) {
      setActionMessage(`Review for ${review.employeeName} reopened.`);
      await notifyReviewReopened(review.employeeId, review.reviewerId, 'HR Admin', selectedCycle?.name || selectedCycleId, reopenReason.trim());
      setReopenReason('');
      setSelectedReviewId('');
      onRefresh?.();
    }
  };

  const cycleStatusLabel = selectedCycle?.isLocked
    ? 'Locked'
    : selectedCycle?.status === ReviewCycleStatus.COMPLETED ? 'Closed' : 'Open';
  const diagnosticFailures = diagnostics.filter((row) => !row.valid).length;
  const maxTrend = Math.max(analytics.completed, 1);

  return (
    <div className="pms-hr-dashboard">
      <div className="pms-hr-dashboard__header">
        <div>
          <span className="pms-hr-dashboard__eyebrow">People analytics</span>
          <h1 className="pms-hr-dashboard__title">Review Performance</h1>
          <p className="pms-hr-dashboard__subtitle">Live review health, completion, ratings, and exceptions.</p>
        </div>
        <div className="pms-hr-dashboard__header-actions">
          <input type="search" placeholder="Search manager" value={managerFilter} onChange={(event) => setManagerFilter(event.target.value)} aria-label="Filter by manager" />
          <select value={reviewTypeFilter} onChange={(event) => setReviewTypeFilter(event.target.value)} aria-label="Filter by review type">
            <option value="">All review types</option>
            <option value={ReviewType.END_YEAR}>Year-End</option>
            <option value={ReviewType.MID_YEAR}>Mid-Year</option>
            <option value={ReviewType.QUARTERLY}>High-Level</option>
          </select>
          <ReviewCycleSelector
            cycles={reviewCycles}
            selectedCycleId={selectedCycleId}
            onChange={onCycleChange}
            filterDescription="All HR-created review cycles are shown. Annual parent cycles are excluded; no status or review-type filter is applied."
          />
          <button type="button" className="pms-hr-dashboard__export-btn" onClick={() => ReportExportService.exportReviewsCSV(filteredReviews, reviewCycles)}>Export CSV</button>
        </div>
      </div>

      {isHistorical && <div className="pms-hr-dashboard__historical-banner">Historical cycle: analytics are read-only for this review period.</div>}

      <div className="pms-hr-dashboard__kpi-grid">
        {[
          ['Total Reviews', analytics.total, 'All filtered review records', 'neutral'],
          ['Completed', `${analytics.completionPercentage}%`, `${analytics.completed} submitted or completed`, 'success'],
          ['Acknowledged', `${analytics.acknowledgementRate}%`, `${analytics.acknowledged} explicitly acknowledged`, 'info'],
          ['Overdue', analytics.overdueRows.length, dueDate ? `Deadline ${formatDate(dueDate)}` : 'No cycle deadline', 'danger'],
          ['Reopen Requests', analytics.reopenRequests, 'Pending HR action', 'warning'],
        ].map(([label, value, detail, tone]) => (
          <article key={String(label)} className={`pms-hr-kpi pms-hr-kpi--${tone}`}>
            <span className="pms-hr-kpi__label">{label}</span>
            <strong>{value}</strong>
            <small>{detail}</small>
          </article>
        ))}
      </div>

      <div className="pms-hr-dashboard__control-bar">
        <div><span>Cycle status</span><strong className={`pms-hr-dashboard__cycle-status pms-hr-dashboard__cycle-status--${cycleStatusLabel.toLowerCase()}`}>{cycleStatusLabel}</strong></div>
        <div className="pms-hr-dashboard__cycle-controls">
          <button type="button" onClick={handleLockCycle} disabled={selectedCycle?.isLocked}>Lock cycle</button>
          <button type="button" onClick={handleUnlockCycle} disabled={!selectedCycle?.isLocked}>Unlock cycle</button>
        </div>
      </div>
      {actionMessage && <div className="pms-hr-dashboard__message" role="status">{actionMessage}</div>}

      <div className="pms-hr-dashboard__analytics-grid">
        <article className="pms-hr-chart-card">
          <div className="pms-hr-chart-card__heading"><div><span>Workflow</span><h2>Review Status Distribution</h2></div><strong>{analytics.statusTotal}</strong></div>
          <div className="pms-hr-status-chart" aria-label="Review status distribution">
            {statusDistribution.map((row) => (
              <div key={row.key} className="pms-hr-status-row">
                <span className="pms-hr-status-row__label">{getReviewWorkflowStatusLabel(row.key)}</span>
                <div className="pms-hr-status-row__track">
                  <i style={{ width: `${row.percentage}%`, backgroundColor: row.color }} />
                </div>
                <strong>{formatChartPercentage(row.percentage)}</strong>
              </div>
            ))}
            <div className="pms-hr-status-row pms-hr-status-row--exception">
              <span className="pms-hr-status-row__label">Overdue</span>
              <div className="pms-hr-status-row__track">
                <i style={{ width: `${calculatePercentage(analytics.overdueRows.length, analytics.total)}%`, backgroundColor: OVERDUE_COLOR }} />
              </div>
              <strong>{formatChartPercentage(calculatePercentage(analytics.overdueRows.length, analytics.total))}</strong>
            </div>
          </div>
          <p className="pms-hr-chart-card__footnote">Workflow segments total 100%. Overdue is shown separately because it overlaps Draft and In Progress statuses.</p>
        </article>

        <article className="pms-hr-chart-card">
          <div className="pms-hr-chart-card__heading"><div><span>Outcomes</span><h2>Rating Distribution</h2></div><strong>{analytics.rated}</strong></div>
          <div className="pms-hr-rating-chart">
            {ratingDistribution.map((row) => <div key={row.rating} className="pms-hr-rating-row"><span>{row.label}</span><div><i style={{ width: `${row.percentage}%`, backgroundColor: row.color }} /></div><strong>{row.count} <small>{formatChartPercentage(row.percentage)}</small></strong></div>)}
          </div>
          <p className="pms-hr-chart-card__footnote">Only reviews with an actual final overall rating are counted.</p>
        </article>

        <article className="pms-hr-chart-card pms-hr-chart-card--wide">
          <div className="pms-hr-chart-card__heading"><div><span>Momentum</span><h2>Review Completion Trend</h2></div><strong>{analytics.completed}</strong></div>
          {completionTrend.length ? <div className="pms-hr-trend-chart">
            {completionTrend.map((point) => <div key={`${point.time}`} className="pms-hr-trend-column"><strong>{point.cumulative} <small>{formatChartPercentage(calculatePercentage(point.cumulative, analytics.total))}</small></strong><div><i style={{ height: `${calculatePercentage(point.cumulative, maxTrend)}%` }} /></div><span>{point.label}</span></div>)}
          </div> : <div className="pms-hr-dashboard__empty-section">No submitted or completed reviews are available for the trend.</div>}
        </article>
      </div>

      <div className="pms-hr-dashboard__insight-row">
        <div className="pms-hr-insight-card">
          <span className="pms-hr-insight-card__icon" aria-hidden="true">!</span>
          <div><span>Action needed</span><strong>{analytics.overdueRows.length + analytics.reopenRequests} items</strong><p>overdue reviews and pending reopen requests require HR follow-up.</p></div>
        </div>
        <div className="pms-hr-insight-card">
          <span className="pms-hr-insight-card__icon" aria-hidden="true">%</span>
          <div><span>Coverage gap</span><strong>{analytics.total - analytics.rated} pending</strong><p>reviews do not yet have a final rating.</p></div>
        </div>
        <div className="pms-hr-insight-card">
          <span className="pms-hr-insight-card__icon" aria-hidden="true">OK</span>
          <div><span>Data confidence</span><strong>{diagnosticFailures ? `${diagnosticFailures} issue${diagnosticFailures === 1 ? '' : 's'}` : 'Validated'}</strong><p>{diagnosticFailures ? 'Open diagnostics for details.' : 'All dashboard totals are internally consistent.'}</p></div>
        </div>
      </div>

      <section className="pms-hr-dashboard__data-section pms-hr-dashboard__data-section--danger">
        <button className="pms-hr-dashboard__section-trigger" onClick={() => setOverdueOpen(!overdueOpen)} aria-expanded={overdueOpen}>
          <span><b>Overdue Reviews</b><small>{analytics.overdueRows.length} records past the cycle deadline</small></span><strong>{overdueOpen ? 'Hide' : 'Show'}</strong>
        </button>
        {overdueOpen && <div className="pms-hr-dashboard__section-content">
          {analytics.overdueRows.length === 0 ? <p className="pms-hr-dashboard__empty-section">No overdue reviews.</p> : <div className="pms-hr-dashboard__table-wrap"><table className="pms-hr-dashboard__table">
            <thead><tr><th>Employee</th><th>Manager</th><th>Review</th><th>Deadline</th><th>Age</th><th>Status</th></tr></thead>
            <tbody>{analytics.overdueRows.map(({ review, daysOverdue }) => {
              const status = inferReviewWorkflowStatus(review);
              return <tr key={review.id} className="pms-hr-dashboard__overdue-row"><td><strong>{review.employeeName}</strong><small>{review.employeeId}</small></td><td><strong>{review.reviewerName}</strong><small>{review.reviewerId}</small></td><td>{review.reviewType}</td><td>{formatDate(dueDate)}</td><td><span className="pms-hr-badge pms-hr-badge--danger">{daysOverdue}d overdue</span></td><td><span className="pms-hr-badge" style={{ color: REVIEW_STATUS_COLORS[status] }}>{getReviewWorkflowStatusLabel(status)}</span></td></tr>;
            })}</tbody>
          </table></div>}
        </div>}
      </section>

      <section className="pms-hr-dashboard__data-section">
        <button className="pms-hr-dashboard__section-trigger" onClick={() => setReopenOpen(!reopenOpen)} aria-expanded={reopenOpen}>
          <span><b>Reopen Requests &amp; Review Actions</b><small>{analytics.reopenRequests} pending request{analytics.reopenRequests === 1 ? '' : 's'}</small></span><strong>{reopenOpen ? 'Hide' : 'Show'}</strong>
        </button>
        {reopenOpen && <div className="pms-hr-dashboard__section-content"><div className="pms-hr-dashboard__table-wrap"><table className="pms-hr-dashboard__table">
          <thead><tr><th>Employee</th><th>Manager</th><th>Status</th><th>Acknowledgement</th><th>Reopen request</th><th>Action</th></tr></thead>
          <tbody>{filteredReviews.map((review) => {
            const status = inferReviewWorkflowStatus(review);
            const canReopen = status === ReviewWorkflowStatus.SUBMITTED || status === ReviewWorkflowStatus.COMPLETED || status === ReviewWorkflowStatus.REOPEN_REQUESTED;
            return <tr key={review.id}><td><strong>{review.employeeName}</strong><small>{review.employeeId}</small></td><td><strong>{review.reviewerName}</strong><small>{review.reviewerId}</small></td><td><span className="pms-hr-badge" style={{ color: REVIEW_STATUS_COLORS[status] }}>{getReviewWorkflowStatusLabel(status)}</span></td><td><span className={`pms-hr-badge ${review.isAcknowledged ? 'pms-hr-badge--success' : ''}`}>{review.isAcknowledged ? 'Acknowledged' : 'Pending'}</span></td><td>{review.reopenRequest?.status || 'None'}</td><td>{canReopen && <button type="button" onClick={() => { setSelectedReviewId(review.id); setReopenReason(review.reopenRequest?.reason || ''); }}>Reopen</button>}</td></tr>;
          })}</tbody>
        </table></div>
        {selectedReviewId && <div className="pms-hr-dashboard__reopen-form"><label htmlFor="hr-reopen-reason">Reason for reopening</label><textarea id="hr-reopen-reason" value={reopenReason} onChange={(event) => setReopenReason(event.target.value)} rows={2} /><button type="button" onClick={handleReopenReview} disabled={!reopenReason.trim()}>Confirm reopen</button></div>}
        </div>}
      </section>

      <section className={`pms-hr-dashboard__data-section ${diagnosticFailures ? 'pms-hr-dashboard__data-section--danger' : 'pms-hr-dashboard__data-section--success'}`}>
        <button className="pms-hr-dashboard__section-trigger" onClick={() => setDiagnosticsOpen(!diagnosticsOpen)} aria-expanded={diagnosticsOpen}>
          <span><b>Metric Diagnostic Report</b><small>Sources, formulas, and reconciliation checks</small></span><strong>{diagnosticFailures ? `${diagnosticFailures} failed` : 'All checks passed'}</strong>
        </button>
        {diagnosticsOpen && <div className="pms-hr-dashboard__section-content"><div className="pms-hr-dashboard__table-wrap"><table className="pms-hr-dashboard__table pms-hr-dashboard__diagnostic-table">
          <thead><tr><th>Metric</th><th>Source table / fields</th><th>Calculation</th><th>Actual</th><th>Expected</th><th>Result</th></tr></thead>
          <tbody>{diagnostics.map((row) => <tr key={row.metric}><td><strong>{row.metric}</strong></td><td>{row.source}</td><td>{row.formula}</td><td>{row.actual}</td><td>{row.expected}</td><td><span className={`pms-hr-badge ${row.valid ? 'pms-hr-badge--success' : 'pms-hr-badge--danger'}`}>{row.valid ? 'Pass' : 'Mismatch'}</span></td></tr>)}</tbody>
        </table></div></div>}
      </section>
    </div>
  );
};

export default HRAdminDashboard;
